import { motion } from "framer-motion";
import { Star, Quote, BadgeCheck, Users } from "lucide-react";

const TestimonialsSection = () => {
  const testimonials = [
    {
      quote:
        "FanJago has completely transformed how we run our academy. What used to take hours now takes minutes. The parents love the transparency too.",
      name: "Ahmad Rahman",
      role: "Academy Director",
      organization: "Selangor United FC",
      avatar: "AR",
      rating: 5,
      featured: false,
    },
    {
      quote:
        "Finally, a system that understands grassroots football. No more Excel nightmares or chasing payments through WhatsApp. It just works.",
      name: "Sarah Lee",
      role: "Club Manager",
      organization: "KL City Youth Academy",
      avatar: "SL",
      rating: 5,
      featured: true,
    },
    {
      quote:
        "As a parent, I love being able to see my son's progress without constantly asking the coach. The payment tracking is a game-changer too.",
      name: "Rizal Hassan",
      role: "Parent",
      organization: "PJ Rangers FC",
      avatar: "RH",
      rating: 5,
      featured: false,
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.section
      className="min-h-screen flex items-center py-12 px-6 bg-surface/30 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
    >
      {/* Background elements */}
      <div className="absolute inset-0 opacity-30">
        <motion.div
          className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-accent/20 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full bg-accent-soft/15 blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Decorative large quote marks */}
      <div className="absolute top-20 left-10 text-accent/5 pointer-events-none">
        <Quote className="w-32 h-32 md:w-48 md:h-48" />
      </div>
      <div className="absolute bottom-20 right-10 text-accent/5 pointer-events-none rotate-180">
        <Quote className="w-32 h-32 md:w-48 md:h-48" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div className="text-center mb-16" variants={fadeInUp}>
          <motion.span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-semibold mb-6">
            <BadgeCheck className="w-4 h-4" />
            What Clubs Are Saying
          </motion.span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Trusted by Football People
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
            Real stories from clubs, coaches, and parents using FanJago
          </p>
        </motion.div>

        {/* Social proof counter */}
        <motion.div
          className="flex items-center justify-center gap-3 mb-12"
          variants={fadeInUp}
        >
          <div className="flex -space-x-3">
            {["AR", "SL", "RH", "MK", "JT"].map((initials, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-accent/30 to-accent/10 border-2 border-background flex items-center justify-center"
              >
                <span className="text-xs font-bold text-accent">
                  {initials}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface/80 border border-border/50">
            <Users className="w-4 h-4 text-accent" />
            <span className="text-sm text-muted-foreground">
              <span className="font-bold text-foreground">50+</span> happy clubs
            </span>
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          className="grid md:grid-cols-3 gap-6 md:gap-8 items-start"
          variants={staggerContainer}
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              className={`relative p-8 rounded-2xl backdrop-blur-sm transition-all duration-500 overflow-hidden
                ${
                  testimonial.featured
                    ? "bg-gradient-to-br from-accent/10 to-accent/5 border-2 border-accent/30 md:-mt-4 md:mb-4 shadow-lg shadow-accent/10"
                    : "bg-background/80 border border-border/50 hover:border-accent/30"
                }`}
              variants={fadeInUp}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              {/* Featured badge */}
              {testimonial.featured && (
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-accent/20 border border-accent/30">
                  <span className="text-xs font-semibold text-accent">
                    Featured
                  </span>
                </div>
              )}

              {/* Decorative corner gradient */}
              <div className="absolute -top-16 -right-16 w-32 h-32 bg-accent/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />

              {/* Quote icon */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 border border-accent/20">
                  <Quote className="w-5 h-5 text-accent" />
                </div>
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-foreground/90 text-lg leading-relaxed mb-8">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div
                  className={`relative w-14 h-14 rounded-full flex items-center justify-center
                  ${
                    testimonial.featured
                      ? "bg-gradient-to-br from-accent to-accent/60"
                      : "bg-accent/20 border border-accent/30"
                  }`}
                >
                  {/* Ring effect for featured */}
                  {testimonial.featured && (
                    <div className="absolute inset-0 rounded-full border-2 border-accent/50 animate-pulse" />
                  )}
                  <span
                    className={`font-bold text-sm ${testimonial.featured ? "text-white" : "text-accent"}`}
                  >
                    {testimonial.avatar}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-foreground flex items-center gap-2">
                    {testimonial.name}
                    {testimonial.featured && (
                      <BadgeCheck className="w-4 h-4 text-accent" />
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.role}
                  </p>
                  <p className="text-xs text-accent">
                    {testimonial.organization}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default TestimonialsSection;
